<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useResizeObserver } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { RefreshCw } from "lucide-vue-next";
import AiStar from "@/components/episto-ui/ai/AiStar.vue";
import { Button } from "@/components/ui/button";
import {
  isGenerating,
  summaryStreamKey,
  useVerbatimSummariesStream,
  type SummaryError,
} from "@composables/verbatimSummariesStream";
import { useSurveyStore } from "@stores/survey/survey";
import { renderToHtml } from "@utils/toolBox";

const props = withDefaults(
  defineProps<{
    questionId: number;
    summaries?: { filters: string; summary: string }[];
    filters?: Record<string, unknown>;
    target?: number | null;
    collapsible?: boolean;
  }>(),
  { summaries: () => [], filters: () => ({}), target: null, collapsible: false },
);

const empty = defineModel<boolean>("empty", { default: false });
const expanded = defineModel<boolean>("expanded", { default: false });
const overflows = defineModel<boolean>("overflows", { default: false });

const { streams, subscribe, generateSummary: generate } = useVerbatimSummariesStream();
const { isComplete } = storeToRefs(useSurveyStore());

subscribe();

const filtersStr = computed(() => JSON.stringify(props.filters || {}));

const streamingState = computed(() => streams[summaryStreamKey(props.questionId, filtersStr.value)] || null);
const isStreaming = computed(() => isGenerating(streamingState.value));
const ERRORS: Record<SummaryError, { message: string; retry: boolean }> = {
  no_verbatims: { message: "No verbatims in this population yet.", retry: false },
  unavailable: { message: "This question has no AI summary.", retry: false },
  timeout: { message: "The summary is taking too long. Try again in a moment.", retry: true },
  failed: { message: "The summary could not be generated.", retry: true },
};
const error = computed(() => {
  const state = streamingState.value;
  return state?.status === "failed" ? ERRORS[state.error || "failed"] || ERRORS.failed : null;
});

const matchingSummary = computed(
  () => props.summaries.find((vs) => vs.filters === filtersStr.value)?.summary || null,
);

const displayedHtml = computed(() => {
  if (isStreaming.value) return renderToHtml((streamingState.value?.text || "") + "▐");
  if (matchingSummary.value) return renderToHtml(matchingSummary.value);
  return "";
});

watch(
  () => !displayedHtml.value,
  (value) => {
    empty.value = value;
  },
  { immediate: true },
);

const showGenerateButton = computed(() => !matchingSummary.value && !isStreaming.value && !error.value);
const showRefreshButton = computed(
  () => !isComplete.value && !!matchingSummary.value && !isStreaming.value && !error.value,
);

const textEl = ref<HTMLElement | null>(null);
function measureOverflow() {
  if (textEl.value && !expanded.value) overflows.value = textEl.value.scrollHeight > textEl.value.clientHeight + 1;
}
useResizeObserver(textEl, measureOverflow);
watch(displayedHtml, measureOverflow, { flush: "post" });

function handleGenerate() {
  generate(props.questionId, props.filters || {}, props.target);
}
</script>

<template>
  <div class="flex flex-col gap-3">
    <div
      v-if="displayedHtml"
      ref="textEl"
      class="prose prose-sm max-w-none text-13 leading-relaxed"
      :class="{ 'line-clamp-2 print:line-clamp-none': collapsible && !expanded }"
      v-html="displayedHtml" />

    <div
      v-if="error"
      class="card-toolbar flex flex-col items-center gap-2 py-3 text-center text-xs text-muted-foreground print:hidden">
      {{ error.message }}
      <Button
        v-if="error.retry"
        variant="outline"
        size="sm"
       
        @click="handleGenerate">
        <RefreshCw class="mr-1.5 h-3.5 w-3.5" />
        Try again
      </Button>
    </div>

    <div v-if="showGenerateButton" class="card-toolbar flex justify-center py-4 print:hidden">
      <Button variant="outline" size="sm" @click="handleGenerate">
        <AiStar class="mr-1.5 h-3.5 w-3.5" />
        Generate summary
      </Button>
    </div>

    <div v-if="showRefreshButton" class="card-toolbar flex justify-center pt-2 print:hidden">
      <Button variant="outline" size="sm" @click="handleGenerate">
        <RefreshCw class="mr-1.5 h-3.5 w-3.5" />
        Refresh with new answers
      </Button>
    </div>
  </div>
</template>
